import { Suspense, lazy, Component, useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import HeroCopy from './HeroCopy';
import { NOTES } from '@/config/site';
import { PERFUME_BRAND_IMAGE, PERFUME_MACRO_VIDEO } from '@/config/assets';
import { useHeroMode } from '@/hooks/useHeroMode';

const PerfumeCanvas = lazy(() => import('./PerfumeCanvas'));

const RANGES = [
  [0.5, 0.615],
  [0.615, 0.72],
  [0.72, 0.825],
  [0.825, 0.91],
];

class SilentBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    return this.state.failed ? null : this.props.children;
  }
}

function NoteOverlay({ progress, note, range, side }) {
  const [a, b] = range;
  const opacity = useTransform(progress, [a, a + 0.035, b - 0.03, b], [0, 1, 1, 0]);
  const y = useTransform(progress, [a, b], [50, -50]);
  const x = useTransform(progress, [a, b], [28 * side, -28 * side]);
  return (
    <motion.div style={{ opacity, y, x }} data-testid={`note-${note.id}`} className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center px-6">
      <div className="text-center">
        <p className="mb-5 text-[10px] uppercase tracking-[0.5em] text-gold">
          Nota {note.index} · {note.stage}
        </p>
        <h3 className="font-display text-[15vw] uppercase leading-none text-cream/95 sm:text-8xl">{note.name}</h3>
        <div className="mx-auto mt-6 h-px w-16 bg-gradient-to-r from-transparent via-gold to-transparent" />
      </div>
    </motion.div>
  );
}

function ImmersiveHero() {
  const ref = useRef(null);
  const stageRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [active, setActive] = useState(true);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end end'] });
  const p = scrollYProgress;
  const copyOpacity = useTransform(p, [0, 0.1, 0.17], [1, 1, 0]);
  const copyY = useTransform(p, [0, 0.18], [0, -70]);
  const cueOpacity = useTransform(p, [0, 0.06], [1, 0]);
  const flashOpacity = useTransform(p, [0.44, 0.495, 0.56], [0, 0.6, 0]);
  const quoteOpacity = useTransform(p, [0.9, 0.965], [0, 1]);
  const quoteY = useTransform(p, [0.9, 0.965], [40, 0]);
  const canvasOpacity = useTransform(p, [0.93, 1], [1, 0.3]);
  const videoScale = useTransform(p, [0, 0.3, 0.47], [1, 1.65, 3.1]);
  const videoOpacity = useTransform(p, [0.42, 0.52], [1, 0]);
  const veilOpacity = useTransform(p, [0.28, 0.46], [0, 0.85]);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(([e]) => setActive(e.isIntersecting), { rootMargin: '120px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section ref={ref} data-testid="perfume-journey" className="relative h-[520vh]">
      <div ref={stageRef} className="sticky top-0 overflow-hidden bg-noir" style={{ height: '100svh' }}>
        <motion.div style={{ opacity: canvasOpacity }} data-testid="journey-canvas" className="pointer-events-none absolute inset-0">
          <div className="h-full w-full" style={{ opacity: ready ? 1 : 0, transition: 'opacity 0.9s ease' }}>
            <SilentBoundary>
              <Suspense fallback={null}>
                <PerfumeCanvas progress={p} active={active} onReady={() => setReady(true)} />
              </Suspense>
            </SilentBoundary>
          </div>
        </motion.div>
        <motion.div
          style={{ scale: videoScale, opacity: videoOpacity, transformOrigin: '50% 42%' }}
          data-testid="hero-video"
          className="pointer-events-none absolute inset-0 will-change-transform"
        >
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            poster={PERFUME_BRAND_IMAGE}
            onCanPlay={(e) => {
              e.currentTarget.play().catch(() => {});
            }}
            onLoadedData={(e) => {
              e.currentTarget.play().catch(() => {});
            }}
            className="h-full w-full object-cover"
            style={{ objectPosition: '50% 35%' }}
          >
            <source src={PERFUME_MACRO_VIDEO} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-t from-noir via-noir/55 to-noir/25" />
        </motion.div>
        <motion.div style={{ opacity: veilOpacity }} data-testid="hero-video-veil" className="pointer-events-none absolute inset-0 bg-noir" />
        <div
          className="pointer-events-none absolute inset-0"
          style={{ background: 'radial-gradient(120% 90% at 50% 42%, transparent 55%, rgba(5,5,6,0.8) 100%)' }}
        />
        <motion.div style={{ opacity: copyOpacity, y: copyY }} className="absolute inset-0">
          <HeroCopy />
        </motion.div>
        {NOTES.map((note, i) => (
          <NoteOverlay key={note.id} progress={p} note={note} range={RANGES[i]} side={i % 2 === 0 ? 1 : -1} />
        ))}
        <motion.div
          style={{ opacity: flashOpacity }}
          data-testid="journey-flash"
          className="pointer-events-none absolute inset-0 z-20 bg-[radial-gradient(75%_55%_at_50%_50%,rgba(212,175,55,0.45),rgba(5,5,6,0)_72%)]"
        />
        <motion.div style={{ opacity: quoteOpacity, y: quoteY }} data-testid="journey-quote" className="absolute inset-0 z-10 flex items-center justify-center px-6">
          <div className="max-w-md text-center">
            <p className="mb-6 text-[10px] uppercase tracking-[0.45em] text-gold">Cristian Cruz</p>
            <p className="font-display text-2xl italic leading-snug text-cream sm:text-4xl">“Detrás de cada venta hay una estrategia.”</p>
          </div>
        </motion.div>
        <motion.div
          style={{ opacity: cueOpacity }}
          data-testid="hero-scroll-cue"
          className="pointer-events-none absolute bottom-6 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-3"
        >
          <span className="text-[9px] uppercase tracking-[0.45em] text-cream/50">Desliza</span>
          <span className="animate-cue block h-10 w-px bg-gradient-to-b from-gold to-transparent" />
        </motion.div>
      </div>
    </section>
  );
}

function StaticHero() {
  return (
    <section data-testid="hero-static" className="relative flex h-screen flex-col justify-end" style={{ height: '100svh' }}>
      <img src={PERFUME_BRAND_IMAGE} alt="Perfume CRISTIAN CRUZ Eau de Parfum" className="absolute inset-0 h-full w-full object-cover opacity-90" />
      <div className="absolute inset-0 bg-gradient-to-t from-noir via-noir/25 to-noir/70" />
      <HeroCopy instant />
    </section>
  );
}

export default function HeroJourney() {
  const mode = useHeroMode();
  return mode === 'static' ? <StaticHero /> : <ImmersiveHero />;
}
